// "Skep bestelling": turns the new spuit program into a normal MyPack plan, plan products and order products.
// Pure logic and orchestration only; the HTTP calls come in as `api` (HybridPlanAPI, the same endpoints the normal screens use).
//
// Plan product  : one per spray row (its own dose and its own ticked weeks), like the normal spuit program.
// Order product : the server makes one per product (it adds the volumes of the rows), then this file overwrites its packs
//                 with the whole boxes the program worked out, so BESTELLING and the price screen show the program's boxes.
// Not priced    : "Ander" and products without a price list entry keep the packs the server worked out (volume only).

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));
const sizeKey = (v) => String(Number(v));

/** The weeks JSON the plan screens store: one {desc, val} per period of the crop. `ticks` is the row's weeks (true/false per period). */
export const weeksFromTicks = (periods, cropId, ticks) => {
  const list = (periods || []).filter((p) => p.crop_id === cropId).sort((a, b) => a.order_seq - b.order_seq);
  if (!list.length) throw new Error("Geen periodes vir hierdie gewas gevind nie");
  return JSON.stringify(list.map((p, i) => ({ desc: p.name, val: !!(ticks || [])[i] })));
};

/**
 * @param computed  computeProgram() result
 * @returns { items, packs, skipped }
 *   items : [{ rowKey, choiceId, productId, name, dosage, volume, ticks, otherName, other, priced }]  one per usable row
 *   packs : Map(productId -> { name, packs: { "<unit_volume>": units } })  the exact boxes, priced products only
 *   skipped: [{name, reason}]
 */
export const programItems = (computed) => {
  const items = [];
  const skipped = [];
  computed.rows.forEach((r) => {
    if (!r.complete) return; // an unfinished row is ignored
    const name = r.other ? (r.row.otherName || "").trim() || "Ander" : r.choice.product;
    if (r.sprays <= 0 || r.dose <= 0) {
      skipped.push({ name, reason: "Geen weke of dosis" });
      return;
    }
    items.push({
      rowKey: r.row.key,
      choiceId: r.choice.choice_id,
      productId: r.choice.product_id,
      name,
      dosage: r.dose,
      volume: r.litres,
      ticks: r.row.weeks,
      otherName: r.other ? name : "",
      other: r.other,
      priced: r.priced,
    });
  });

  const packs = new Map();
  computed.lines.forEach((l) => {
    const row = computed.rows.find((r) => r.pack && r.pack.id === l.rowId);
    if (!row || l.boxes <= 0) return;
    const pack = row.pack;
    // The server keys packs by UNIT size (pricing2.unit_volume, e.g. 5 for a 4 x 5 L box) and counts UNITS
    const size = num(pack.unit_volume) || num(pack.full_pack_volume);
    const unitsPerBox = num(pack.full_pack_units) || 1;
    if (!Number.isInteger(size) || size <= 0) {
      skipped.push({ name: pack.product_name, reason: `Pakgrootte ${size} is nie 'n heelgetal nie` });
      return;
    }
    const id = row.choice.product_id;
    const entry = packs.get(id) || { name: pack.product_name, packs: {} };
    entry.packs[sizeKey(size)] = (entry.packs[sizeKey(size)] || 0) + l.boxes * unitsPerBox;
    packs.set(id, entry);
  });
  return { items, packs, skipped };
};

export const plantingBody = (planName, hectares, cropName) => ({
  programName: planName,
  cultivar: "Spuitprogram",
  crop: cropName || "",
  farmSize: Math.max(1, Math.round(hectares)),
  rowWidth: 0,
  circles: "Spuitprogram",
});

export const createProgramPlan = async ({ api, cropId, planting, planName, built, periods, now = Date.now() }) => {
  const { items, packs } = built;
  if (!items.length) throw new Error("Daar is geen volledige spuitreëls nie");
  const plantingId = (await api.createPlanting(planting.farmerId, planting.body)).id;
  const plan = await api.createPlan({ planting_id: plantingId, planName, plantingDate: now, archived: false, orderStatus_id: 1, crop_id: cropId });
  try {
    for (const it of items) {
      await api.createPlanProduct({
        planProduct: {
          plan_id: plan.id,
          dosage: it.dosage,
          totalVolume: it.volume,
          weeks: weeksFromTicks(periods, cropId, it.ticks),
          choice_id: it.choiceId,
          crop_id: cropId,
        },
        planProductOther: it.other ? { name: it.otherName, new: true } : { name: "n/a", new: true },
      });
    }
    // The server worked out its own pack mix from the volumes. Replace it with the program's whole boxes.
    const orderProducts = await api.getOrderProducts(plan.id);
    for (const [productId, want] of packs) {
      const op = orderProducts.find((o) => o.product_id === productId);
      if (!op) throw new Error(`Bestelproduk vir ${want.name} nie gevind nie`);
      const current = JSON.parse(op.packages || "{}");
      const known = new Set(Object.keys(current).map(sizeKey));
      const unknown = Object.keys(want.packs).filter((k) => !known.has(k));
      if (unknown.length) throw new Error(`${want.name}: pakgrootte ${unknown.join(", ")} is nie 'n verpakking van die produk nie (die bediener ken ${[...known].join(", ")})`);
      for (const key of new Set([...known, ...Object.keys(want.packs)])) {
        const qty = want.packs[key] || 0;
        if (num(current[key]) !== qty) await api.setPacks(op.id, Number(key), qty);
      }
    }
  } catch (e) {
    e.planId = plan.id;
    e.planName = planName;
    throw e;
  }
  return { plan };
};
